import { formatInTimeZone } from "date-fns-tz";
import type { WorldClockEntry } from "@multisystemsuite/timezone-engine-shared-types";
import { getTimezoneInfo, listAllTimezones } from "@multisystemsuite/timezone-engine-world-data";
import { detectDST, getUtcOffset } from "./conversion.js";

function buildClockEntry(timezone: string, label: string, now: Date): WorldClockEntry {
  const info = getTimezoneInfo(timezone);
  const formattedTime = formatInTimeZone(now, timezone, "PPpp");
  const utcOffset = getUtcOffset(timezone, now);
  const isDST = detectDST(timezone, now);

  return {
    timezone,
    label: label || info?.city || timezone,
    localTime: now,
    formattedTime,
    utcOffset,
    isDST,
  };
}

/** Get world clock for a single timezone */
export function getWorldClockTime(
  timezone: string,
  label?: string,
  now: Date = new Date(),
): WorldClockEntry {
  return buildClockEntry(timezone, label ?? timezone, now);
}

/** Get world clock for multiple timezones */
export function getWorldClock(timezones: string[], now: Date = new Date()): WorldClockEntry[] {
  return timezones.map((tz) => buildClockEntry(tz, tz, now));
}

/** Get default world clock with major cities */
export function getDefaultWorldClock(now: Date = new Date()): WorldClockEntry[] {
  const defaults = [
    "UTC",
    "America/New_York",
    "Europe/London",
    "Asia/Kolkata",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];
  return getWorldClock(defaults, now);
}

/** Get all registered world clock entries */
export function getFullWorldClock(now: Date = new Date()): WorldClockEntry[] {
  return getWorldClock(listAllTimezones(), now);
}

/** Create a live clock updater callback */
export function createLiveClockUpdater(
  timezones: string[],
  onUpdate: (clocks: WorldClockEntry[]) => void,
  intervalMs = 1000,
): { start: () => void; stop: () => void } {
  let timerId: ReturnType<typeof setInterval> | undefined;

  const tick = () => {
    onUpdate(getWorldClock(timezones));
  };

  return {
    start: () => {
      tick();
      timerId = setInterval(tick, intervalMs);
    },
    stop: () => {
      if (timerId !== undefined) {
        clearInterval(timerId);
        timerId = undefined;
      }
    },
  };
}
